import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import {
  deleteKlantgroepActionCreator,
  getKlantgroepenActionCreator,
  IDeleteKlantGroepResponseAction,
  IGetKlantGroepResponseAction,
  IPostKlantGroepResponseAction,
  postKlantgroepActionCreator,
} from 'redux/store/actions/klantGroepenAction'
import { IApplicationState } from 'redux/store/store'
import { Loader } from 'shared/components/Loader'
import MessageModal from 'shared/components/MessageModal'
import { Answer, MessageType, VestigingEnum } from 'shared/models/Enum'
import { IKlantGroepModel } from 'shared/models/IKlantGroepModel'
import { IKlantGroepSelectie } from 'shared/models/IKlantGroepSelectie'
import { IOrderType } from 'shared/models/IOrderType'
import SearchForm from './Components/SearchForm'
import ErrorMessages from './Components/ErrorMessages'
import ResultsSection from './Components/ResultsSection'
import Title from './Components/Title'
import DetailsModal from './Components/DetailsModal/DetailsModal'
import { IGetOverflowTimeslotsFilters } from 'shared/models/IGetOverflowTimeslotsFilters'
import {
  getAllVasCodesForWarehouseActionCreator,
  getOverflowTimeslotsActionCreator,
  getPossibleOrderTypesActionCreator,
  IGetAllVasCodesForWarehouseResponseAction,
  IGetOverflowTimeslotsResponseAction,
  ISetPossibleOrderTypesResponseAction,
} from 'redux/store/actions/globalAction'
import {
  getInitialDays,
  getInitialProducts,
  validateDefaultTimeSlots,
} from './Components/DetailsModal/utils/utils'

interface IProps {
  selectie: IKlantGroepSelectie
  klantGroepen: IKlantGroepModel[]
  isLoading: boolean
  globalVestiging: VestigingEnum
  possibleOrderTypes: IOrderType[]
  loadingPossibleOrderTypes: boolean
  getKlantgroepen: (
    selectie: IKlantGroepSelectie
  ) => Promise<IGetKlantGroepResponseAction>
  postKlantgroep: (
    klantgroep: IKlantGroepModel
  ) => Promise<IPostKlantGroepResponseAction>
  deleteKlantgroep: (
    klantgroep: IKlantGroepModel
  ) => Promise<IDeleteKlantGroepResponseAction>
  getOverflowTimeslots: (
    filters: IGetOverflowTimeslotsFilters
  ) => Promise<IGetOverflowTimeslotsResponseAction>
  getAllVasCodesForWarehouse: (
    warehouseId: number
  ) => Promise<IGetAllVasCodesForWarehouseResponseAction>
  getPossibleOrderTypes: () => Promise<ISetPossibleOrderTypesResponseAction>
}

const KlantGroepenLijst: React.FunctionComponent<IProps> = ({
  globalVestiging,
  selectie = { code: undefined, vestiging: globalVestiging },
  isLoading,
  klantGroepen,
  possibleOrderTypes,
  loadingPossibleOrderTypes,
  getKlantgroepen,
  postKlantgroep,
  deleteKlantgroep,
  getOverflowTimeslots,
  getAllVasCodesForWarehouse,
  getPossibleOrderTypes,
}) => {
  const [show, setShow] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>('')
  const [currentKlantGroep, setCurrentKlantGroep] =
    useState<IKlantGroepModel | null>(null)
  const [deleteWaitingForConfirmation, setDeleteWaitingForConfirmation] =
    useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const validationWarning = useMemo(
    () =>
      validateDefaultTimeSlots(
        klantGroepen,
        selectie,
        getInitialDays(),
        getInitialProducts()
      ),
    [klantGroepen, selectie.code]
  )

  // Fetch possible order types
  useEffect(() => {
    getPossibleOrderTypes().then(result => {
      if (!result.success) {
        setErrorMessage(result.errorMessage)
      }
    })
  }, [getPossibleOrderTypes])

  const showDetails = (klantGroep: IKlantGroepModel) => {
    setIsSaving(false)
    setSaveErrorMessage('')
    setCurrentKlantGroep(klantGroep)
    setShow(true)
  }

  const closeDetails = () => {
    setDeleteWaitingForConfirmation(false)
    getKlantgroepen(selectie)
    setIsSaving(false)
    setSaveErrorMessage('')
    setShow(false)
  }

  const searchKlantGroepen = (code: string) => {
    if (possibleOrderTypes?.length <= 0) {
      return
    }

    getKlantgroepen({
      ...selectie,
      vestiging: globalVestiging,
      code: code ?? selectie.code,
    })
  }

  const getDeleteMessage = () => {
    return 'Weet je zeker dat je het tijdvak wilt verwijderen? Wanneer je het tijdvak verwijderd hebt, worden alle koppelingen tussen AAR, Klant en dit tijdvak verwijderd.'
  }

  const getDeleteMessageHeader = () => {
    return `${
      currentKlantGroep?.defaultIndicatie === 'J' ? 'Default' : ''
    } Tijdvak: ${currentKlantGroep?.klantGroepOmschrijving} Verwijderen?`
  }

  const deleteGroup = (answer: Answer) => {
    if (answer === Answer.Yes) {
      deleteKlantgroep(currentKlantGroep).then(
        (result: { payload: string }) => {
          if (result.payload === '') {
            setCurrentKlantGroep(null)
            closeDetails()
          } else {
            setShow(true)
          }
        }
      )
    }
    setDeleteWaitingForConfirmation(false)
  }

  const handleSave = async (klantGroep: Partial<IKlantGroepModel>) => {
    setSaveErrorMessage('')
    setIsSaving(true)
    const postKlantGroepResult = await postKlantgroep(
      klantGroep as IKlantGroepModel
    )
    setIsSaving(false)
    if (postKlantGroepResult.success) {
      closeDetails()
    } else {
      setSaveErrorMessage(postKlantGroepResult.errorMessage)
    }
  }

  const handleDelete = () => {
    setShow(false)
    setDeleteWaitingForConfirmation(true)
  }

  const handleNieuw = (isDefault: boolean) => {
    const newKlantGroep: Partial<IKlantGroepModel> = {
      vrijgaveTijdstip: '06:00',
      defaultIndicatie: isDefault ? 'J' : 'N',
      dagen: [1, 2, 3, 4, 5, 6, 0].join(','),
      orderType: possibleOrderTypes?.map(type => type.value).join(','),
      verrekenIndicatie: 'N',
      ordertijdIndicatie: 'N',
      productType: 'A',
      vasCode: { code: '', config: {} },
      uitersteInkoopTijdstipIndicatie: false,
      pilotIndicatie: false,
      tijdvakKosten: 0.0,
      eddWorkDaysAfterLSD: 0,
      shipmentDaysBeforeDelivery: 0,
    }

    showDetails(newKlantGroep as IKlantGroepModel)
  }

  if (globalVestiging == null) {
    return <Redirect to='/home' />
  }

  return (
    <main>
      <Loader show={isLoading || loadingPossibleOrderTypes} />
      <Title>Onderhouden Tijdvakken</Title>
      <SearchForm
        defaultCode={selectie.code}
        locationName={VestigingEnum[globalVestiging]}
        onSearch={searchKlantGroepen}
      />
      <ErrorMessages
        errorMessage={errorMessage}
        validationWarning={validationWarning}
      />
      <ResultsSection
        klantGroepen={klantGroepen}
        productTypes={getInitialProducts()}
        possibleOrderTypes={possibleOrderTypes}
        showDetails={showDetails}
        handleNieuw={handleNieuw}
      />
      {show && (
        <DetailsModal
          show={show}
          initialData={currentKlantGroep}
          globalVestiging={globalVestiging}
          possibleOrderTypes={possibleOrderTypes}
          isSaving={isSaving}
          saveErrorMessage={saveErrorMessage}
          onClose={closeDetails}
          onSave={handleSave}
          onDelete={handleDelete}
          getOverflowTimeslots={getOverflowTimeslots}
          getAllVasCodesForWarehouse={getAllVasCodesForWarehouse}
        />
      )}

      <MessageModal
        showModal={deleteWaitingForConfirmation}
        title={getDeleteMessageHeader()}
        message={getDeleteMessage()}
        messageType={MessageType.YesNo}
        onAnswer={deleteGroup}
      />
    </main>
  )
}

const mapStateToProps = (store: IApplicationState) => {
  return {
    isLoading: store.klantGroepState.loading,
    selectie: store.klantGroepState.selectie,
    klantGroepen: store.klantGroepState.klantGroepen,
    foutmelding: store.klantGroepState.foutmelding,
    globalVestiging: store.globalState.vestiging,
    possibleOrderTypes: store.globalState.possibleGroupLinkOrderTypes,
    loadingPossibleOrderTypes: store.globalState.loadingOrderTypes,
  }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    getKlantgroepen: (selectie: IKlantGroepSelectie) =>
      dispatch(getKlantgroepenActionCreator(selectie)),
    postKlantgroep: (klantgroep: IKlantGroepModel) =>
      dispatch(postKlantgroepActionCreator(klantgroep)),
    deleteKlantgroep: (klantgroep: IKlantGroepModel) =>
      dispatch(deleteKlantgroepActionCreator(klantgroep)),
    getOverflowTimeslots: (filters: IGetOverflowTimeslotsFilters) =>
      dispatch(getOverflowTimeslotsActionCreator(filters)),
    getAllVasCodesForWarehouse: (warehouseId: number) =>
      dispatch(getAllVasCodesForWarehouseActionCreator(warehouseId)),
    getPossibleOrderTypes: () => dispatch(getPossibleOrderTypesActionCreator()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(KlantGroepenLijst)
